/**
 * src/components/features/renfest/printUpgrade.jsx
 *
 * The one place a guest can turn their portrait into a thing they carry home,
 * and the only place on the booth where money is asked for.
 *
 * ⚠️ THE METRIC IS NOT CHECKOUT CONVERSION. It is REDEMPTION AT BOOTH #383.
 * docs/CTA-PRINT-UPGRADE.md, and Joel 2026-09-02: at a low price the print
 * earns almost nothing but it puts a person in front of a Shaman staffer who
 * can talk to them. A version of this that sells more prints and gets fewer
 * people to the counter is a LOSS. That is why the copy names the booth twice
 * and why "Pick it up today" is not decoration.
 *
 * ⚠️ IT APPEARS AFTER THE ADDRESS IS CAPTURED, NEVER BEFORE. Same gate as
 * Share, and the parent enforces it. Two reasons, and both are the same reason:
 *   · The `done` screen's own comment says its one job is getting the address.
 *     An ask for money above that competes with the ask that pays for
 *     everything, and the email is the business.
 *   · The microcopy is "Your portrait is yours to keep. Want it on paper?" —
 *     which is only TRUE once it is theirs. Showing it first makes it a
 *     paywall, and a paywall in front of a free portrait is a different
 *     product from the one anyone agreed to run.
 *
 * ⚠️ ONE ENTRANCE, NO LOOP. `rf-rise` plays once, half a second, and then this
 * sits still. The booth already has `rf-sweep` and `rf-halo` on the things that
 * are genuinely WAITING for a tap; a payment CTA that pulses forever is not
 * emphasis, it is pressure. NN/g: interface animation must be "unobtrusive,
 * brief, and subtle", and using it to hijack attention or manufacture urgency
 * is a dark pattern. It is also the one element on this screen a guest may
 * decline without being nagged.
 *
 * ⚠️ THE PRICE IS FETCHED, NEVER WRITTEN HERE. It is per-arm
 * (netlify/functions/_pricing.js), so a literal in this file is wrong for half
 * the guests the moment a second arm is switched on. The GET is cheap by
 * design — one row, mints nothing. Until it answers, this component renders
 * NOTHING: a button that says "$—" or a grey skeleton is worse than a beat of
 * absence on a screen the guest is already reading.
 *
 * ⚠️ THE PRICE IS QUOTED PRE-TAX, AND THAT IS CORRECT RATHER THAN AN OVERSIGHT.
 * Shaman's register adds tax on top — a $3.00 print rang up at $3.28 on
 * 2026-09-02 — and their checkout shows the guest the tax before they pay.
 * Every American retail price on a shelf works this way, so "plus tax" on the
 * button buys nothing and costs words on a CTA a guest is reading while
 * standing in a field. Joel, 2026-09-03: *"Everyone's used to paying tax on
 * products... that's a normal cost of business and consumer buying."*
 *   ⚠️ The surprise-cost problem this was guarding against is REAL, but it is
 * about shipping and fees — a charge the guest could not have predicted. Tax on
 * a physical thing bought in person is predicted by everybody. Do not confuse
 * the two and re-add this.
 */
import { useEffect, useState } from 'react';
import { Printer, MapPin, AlertCircle, Check } from 'lucide-react';

/**
 * @param {object}   props
 * @param {string}   props.jobId      the run to buy a print of
 * @param {Function} props.getOffer   () => Promise<offer>   — api.booth.printOffer
 * @param {Function} props.startCheckout () => Promise<{url}> — api.booth.printCheckout
 * @param {string}  [props.where]     where to collect it, e.g. "Booth #383"
 */
export default function PrintUpgrade({
  jobId,
  getOffer,
  startCheckout,
  where = 'Booth #383 — CBD American Shaman',
}) {
  const [offer, setOffer] = useState(null);
  const [phase, setPhase] = useState('idle');   // idle | working | paid | failed
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    if (!jobId) return undefined;
    (async () => {
      try {
        const o = await getOffer(jobId);
        if (!alive) return;
        if (o?.ok && o.ready) {
          setOffer(o);
          if (o.already_paid) setPhase('paid');
        }
      } catch {
        /* ⚠️ SILENT, AND DELIBERATELY SO. If the offer cannot be read, the
           guest simply never sees an upsell — the portrait, the email and the
           share all still work, and the counter can still sell them a print in
           person. An error message here would be us apologising for something
           they never knew was supposed to happen. api.js has already filed the
           failure in platform_error. */
      }
    })();
    return () => { alive = false; };
  }, [jobId, getOffer]);

  if (!offer) return null;

  if (phase === 'paid') {
    return (
      <div className="rf-rise" style={{ textAlign: 'center', padding: '.75rem 0 .25rem' }}>
        <p className="rf-ink" style={{ margin: 0, fontSize: '1.05rem' }}>
          <Check size={18} className="inline mr-1" />
          Paid — your print is on its way
        </p>
        <p className="rf-plain" style={{ margin: '.25rem 0 0', fontSize: '.9rem', opacity: .75 }}>
          Collect it at {where}.
        </p>
      </div>
    );
  }

  async function buy() {
    setPhase('working');
    setError('');
    try {
      const res = await startCheckout(jobId);
      if (res?.already_paid) { setPhase('paid'); return; }
      if (!res?.url) throw new Error(res?.error || 'Checkout would not open.');
      /* ⚠️ FULL NAVIGATION, NOT window.open. A popup opened from an async
         callback is blocked by every mobile browser there is — the tap has
         already "finished" by the time the link exists. Navigating away is
         safe here: the portrait is already in their inbox and the print
         releases from the webhook, so nothing on this screen is still needed. */
      window.location.assign(res.url);
    } catch (e) {
      setPhase('failed');
      setError(e.serverError || e.message
        || 'That would not open. You can still buy it at the booth.');
    }
  }

  return (
    <div className="rf-rise" style={{ marginTop: '.75rem' }}>
      {/* ⚠️ "YOURS TO KEEP" FIRST, AND IT IS DOING REAL WORK. It settles that
          nothing is being taken away before it asks for anything, which is what
          separates an upsell from a paywall. Tested copy, docs/CTA-PRINT-UPGRADE.md. */}
      <p className="rf-ink" style={{ margin: '0 0 .4rem', fontSize: '1rem', opacity: .85, textAlign: 'center' }}>
        Your portrait is yours to keep. Want it on paper?
      </p>

      <button
        type="button"
        onClick={buy}
        disabled={phase === 'working'}
        className="rf-big w-full rounded-2xl px-4 py-4"
        style={{ fontSize: '1.2rem', opacity: phase === 'working' ? .6 : 1 }}
      >
        <Printer size={21} className="inline mr-2" />
        {phase === 'working'
          ? 'Opening checkout…'
          /* ⚠️ THE PRICE IS IN THE BUTTON, not above it and not on the next
             screen. A CTA that hides its price until the checkout loads is the
             thing Baymard measures people abandoning. */
          : `Print My 5x7 Portrait — ${offer.price}`}
      </button>

      {/* ⚠️ THE FULFILMENT PROMISE, AND THE BOOTH NAME IS THE POINT OF IT.
          "Pick it up today" is what makes this different from every other
          photo upsell a guest has ever seen, and naming the booth is what
          turns a sale into footfall. Do not shorten this to save a line. */}
      <p className="rf-plain" style={{
        margin: '.5rem 0 0', fontSize: '.85rem', lineHeight: 1.5,
        opacity: .75, textAlign: 'center',
      }}>
        A real 5x7 print in a keepsake display card.<br />
        <MapPin size={13} className="inline mr-1" />
        Pick it up today at {where}. Usually ready in about 15 minutes.<br />
        Secure checkout · {offer.price} · nothing to ship.
      </p>

      {phase === 'failed' && (
        /* rf-plain: this is the one line on the screen a guest reads word by
           word, and MedievalSharp is a liability on anything that matters. */
        <p className="rf-plain" style={{
          color: '#ffb4a2', fontSize: '.9rem', margin: '.5rem 0 0', textAlign: 'center',
        }}>
          <AlertCircle size={14} className="inline mr-1" />{error}
        </p>
      )}
    </div>
  );
}
